import { useMemo, useState } from "react";
import { Filter, RefreshCw, Settings2, Sparkles } from "lucide-react";

import { useAnalysePolicyBatch, useInventoryPolicies } from "@/api/queries";
import type { InventoryPolicy, PolicyMode } from "@/api/types";
import { Button } from "@/components/Button";
import { Pagination, TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Select } from "@/components/Input";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import {
  formatPolicyMode,
  formatThreshold,
  formatUnit,
  pluralize,
} from "@/lib/format";
import { policyModeTone } from "@/lib/stock";
import { PolicyDetailPanel } from "./PolicyDetailPanel";

const PAGE_SIZE = 50;
const MODES: PolicyMode[] = ["MANUAL", "AUTO_WITH_APPROVAL", "AUTO"];

/**
 * Les seuils de réapprovisionnement (V1.6-03).
 *
 * L'écran montre ce qui est en vigueur, ce que le moteur propose, et pourquoi.
 * Il ne montre **jamais** un achat : le mode « Auto » porte sur des seuils, et
 * rien de ce qui se passe ici ne crée un bon de commande, ne réserve ni ne
 * déplace du stock.
 */
export function PoliciesPage() {
  const toast = useToast();
  const [offset, setOffset] = useState(0);
  const [mode, setMode] = useState<PolicyMode | "">("");
  const [selected, setSelected] = useState<InventoryPolicy | null>(null);

  const query = useMemo(
    () => ({ mode: mode || undefined, limit: PAGE_SIZE, offset }),
    [mode, offset],
  );
  const { data, isLoading, error, refetch } = useInventoryPolicies(query);
  const analyse = useAnalysePolicyBatch();

  async function analyseAll() {
    try {
      const result = await analyse.mutateAsync({ limit: PAGE_SIZE });
      toast.success(
        "Analyse terminée",
        result.created.length > 0
          ? `${pluralize(result.created.length, "suggestion")} à vérifier sur ${result.matched} politique(s).`
          : "Aucun seuil ne demande à changer.",
      );
    } catch (analyseError) {
      toast.error("Analyse impossible", String(analyseError));
    }
  }

  return (
    <Page>
      <PageHeader
        title="Seuils de réapprovisionnement"
        subtitle="Ce qui est en vigueur, ce que les chiffres suggèrent, et pourquoi. Aucun achat n'est déclenché ici."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void refetch()}
              loading={isLoading}
            >
              <RefreshCw className="size-4" aria-hidden />
              Rafraîchir
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void analyseAll()}
              loading={analyse.isPending}
            >
              <Sparkles className="size-4" aria-hidden />
              Analyser
            </Button>
          </div>
        }
      />

      <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
        <div className="flex shrink-0 flex-col gap-2 border-b border-border p-3 sm:flex-row sm:items-center">
          <Select
            value={mode}
            onChange={(event) => {
              setMode(event.target.value as PolicyMode | "");
              setOffset(0);
            }}
            aria-label="Filtrer par mode"
            className="sm:w-64"
          >
            <option value="">Tous les modes</option>
            {MODES.map((value) => (
              <option key={value} value={value}>
                {formatPolicyMode(value)}
              </option>
            ))}
          </Select>
        </div>

        <div className="min-w-0">
          {isLoading && <TableSkeleton rows={8} columns={7} />}
          {error && <ErrorState error={error} onRetry={() => void refetch()} />}

          {data && data.items.length === 0 && (
            <EmptyState
              icon={<Filter className="size-6" aria-hidden />}
              title="Aucune politique"
              description="Posez un minimum et un maximum sur une fiche produit ; le moteur pourra ensuite les remettre en question."
            />
          )}

          {data && data.items.length > 0 && (
            <>
              <div className="hidden md:block">
                <TableShell>
                  <thead>
                    <tr>
                      <Th className="max-w-0">Produit</Th>
                      <Th className="hidden max-w-0 @3xl:table-cell">Emplacement</Th>
                      <Th>Mode</Th>
                      <Th align="right" className="w-20 whitespace-nowrap">
                        MIN
                      </Th>
                      <Th align="right" className="w-20 whitespace-nowrap">
                        MAX
                      </Th>
                      <Th align="right" className="hidden w-28 whitespace-nowrap @4xl:table-cell">
                        Pt commande
                      </Th>
                      <Th align="right" className="hidden w-28 whitespace-nowrap @5xl:table-cell">
                        Sécurité
                      </Th>
                      <Th className="w-32">Suggestion</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((policy) => (
                      <PolicyRow
                        key={policy.id}
                        policy={policy}
                        onOpen={() => setSelected(policy)}
                      />
                    ))}
                  </tbody>
                </TableShell>
              </div>

              <ul className="divide-y divide-border/60 md:hidden">
                {data.items.map((policy) => (
                  <li key={policy.id}>
                    <PolicyCard policy={policy} onOpen={() => setSelected(policy)} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination
            offset={offset}
            limit={PAGE_SIZE}
            total={data.total}
            onChange={setOffset}
          />
        )}
      </div>

      <PolicyDetailPanel policy={selected} onClose={() => setSelected(null)} />
    </Page>
  );
}

function Suggestion({ policy }: { policy: InventoryPolicy }) {
  if (!policy.pending_recommendation_id) {
    return <span className="text-[12px] text-ink-subtle">—</span>;
  }
  return <StatusBadge tone="warning">À vérifier</StatusBadge>;
}

function PolicyRow({ policy, onOpen }: { policy: InventoryPolicy; onOpen: () => void }) {
  const unit = policy.stock_unit ? formatUnit(policy.stock_unit) : "";
  return (
    <Tr>
      <Td className="max-w-0">
        <button
          type="button"
          onClick={onOpen}
          className="block min-w-0 max-w-full text-left hover:text-brand"
        >
          <span className="block truncate font-medium text-ink">
            {policy.product_name ?? "—"}
          </span>
          {policy.product_part_number && (
            <span className="block truncate font-mono text-[11px] text-ink-subtle">
              {policy.product_part_number}
            </span>
          )}
        </button>
      </Td>
      <Td className="hidden max-w-0 truncate text-[13px] text-ink-muted @3xl:table-cell">
        {/* Nul veut dire « partout » : le dire, plutôt qu'une cellule vide qui
            ressemble à une donnée manquante. */}
        {policy.location_name ?? "Partout"}
      </Td>
      <Td>
        <StatusBadge tone={policyModeTone(policy.mode ?? "MANUAL")}>
          {formatPolicyMode(policy.mode ?? "MANUAL")}
        </StatusBadge>
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px]">
        {formatThreshold(policy.min_quantity)}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px]">
        {formatThreshold(policy.max_quantity)} {unit}
      </Td>
      <Td
        align="right"
        className="tnum hidden whitespace-nowrap text-[13px] text-ink-muted @4xl:table-cell"
      >
        {formatThreshold(policy.reorder_point)}
      </Td>
      <Td
        align="right"
        className="tnum hidden whitespace-nowrap text-[13px] text-ink-muted @5xl:table-cell"
      >
        {formatThreshold(policy.safety_stock)}
      </Td>
      <Td>
        <button type="button" onClick={onOpen} className="hover:opacity-80">
          <Suggestion policy={policy} />
        </button>
      </Td>
    </Tr>
  );
}

/**
 * Sur mobile, une carte plutôt qu'une table de huit colonnes.
 *
 * Ce qu'on montre en premier : le produit, l'emplacement, les seuils actuels,
 * et s'il y a quelque chose à vérifier. Le reste s'ouvre.
 */
function PolicyCard({ policy, onOpen }: { policy: InventoryPolicy; onOpen: () => void }) {
  const unit = policy.stock_unit ? formatUnit(policy.stock_unit) : "";
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full min-h-[64px] items-center gap-3 px-3 py-3 text-left active:bg-surface-hover"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">
          {policy.product_name ?? "—"}
        </p>
        <p className="truncate text-[12px] text-ink-muted">
          {policy.location_name ?? "Partout"} · MIN {formatThreshold(policy.min_quantity)} / MAX{" "}
          {formatThreshold(policy.max_quantity)} {unit}
        </p>
        <p className="mt-0.5 text-[11px] text-ink-subtle">
          {formatPolicyMode(policy.mode ?? "MANUAL")}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Suggestion policy={policy} />
        <Settings2 className="size-4 text-ink-subtle" aria-hidden />
      </div>
    </button>
  );
}
